import { useState } from "react";

import {
  Box,
  Grid,
  Chip,
  List,
  Menu,
  MenuItem,
  Typography,
  IconButton,
  Tooltip,
  Divider,
  Avatar,
  ListItemAvatar,
  ListItemText,
  ListItem as MuiListItem
} from "@mui/material";
import FaceIcon from "@mui/icons-material/Face";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";

import { nameToInitials } from "@aclymatepackages/converters";

export const ROLE_CONFIG = {
  admin: { label: "Admin", color: "success" },
  sharing: { label: "Sharing", color: "info" },
  editor: { label: "Editor", color: "warning" },
  viewer: { label: "Viewer", color: "default" }
};

export const ROLE_OPTIONS = [
  { value: "admin", label: "Admin", description: "Full access" },
  {
    value: "sharing",
    label: "Sharing",
    description: "Share & accept survey data"
  },
  {
    value: "editor",
    label: "Editor",
    description: "Edit transactions, offices, employees, products"
  },
  { value: "viewer", label: "Viewer", description: "Read-only access" }
];

const RoleChip = ({ role, canChange, onSelect }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const effectiveRole = role ?? "admin";
  const { label, color } = ROLE_CONFIG[effectiveRole] || ROLE_CONFIG.viewer;

  return (
    <>
      <Chip
        label={label}
        color={color}
        size="small"
        onClick={canChange ? (e) => setAnchorEl(e.currentTarget) : undefined}
        style={{ cursor: canChange ? "pointer" : "default" }}
      />
      {canChange && (
        <Menu
          anchorEl={anchorEl}
          open={!!anchorEl}
          onClose={() => setAnchorEl(null)}
        >
          {ROLE_OPTIONS.map(({ value, label: optLabel, description }) => (
            <MenuItem
              key={value}
              selected={value === effectiveRole}
              onClick={() => {
                onSelect(value);
                setAnchorEl(null);
              }}
            >
              <Box>
                <Typography variant="body2">{optLabel}</Typography>
                <Typography variant="caption" color="textSecondary">
                  {description}
                </Typography>
              </Box>
            </MenuItem>
          ))}
        </Menu>
      )}
    </>
  );
};

const UserRow = ({
  user,
  currentUser,
  isCurrentUserAdmin,
  onRoleChange,
  onRemove
}) => {
  const { authUid, name, email, role, id } = user;
  const [isRemoving, setIsRemoving] = useState(false);
  const isSelf = authUid === currentUser?.uid;

  return (
    <>
      <MuiListItem>
        <ListItemAvatar>
          <Avatar>{name ? nameToInitials(name) : <FaceIcon />}</Avatar>
        </ListItemAvatar>
        <ListItemText
          primary={isRemoving ? "Remove this user?" : name || email}
          secondary={!isRemoving && name ? email : null}
          primaryTypographyProps={{ color: "textPrimary" }}
        />
        <Grid
          container
          spacing={1}
          alignItems="center"
          justifyContent="flex-end"
          wrap="nowrap"
        >
          {!isRemoving && (
            <Grid item>
              <RoleChip
                role={role}
                canChange={isCurrentUserAdmin && !isSelf}
                onSelect={(newRole) => onRoleChange(id, newRole)}
              />
            </Grid>
          )}
          {isCurrentUserAdmin && (
            <Grid item>
              {isRemoving ? (
                <Grid container wrap="nowrap">
                  <Tooltip title="Confirm">
                    <IconButton
                      size="small"
                      onClick={() => {
                        onRemove(user);
                        setIsRemoving(false);
                      }}
                    >
                      <CheckCircleIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Cancel">
                    <IconButton
                      size="small"
                      onClick={() => setIsRemoving(false)}
                    >
                      <CancelIcon />
                    </IconButton>
                  </Tooltip>
                </Grid>
              ) : (
                <Tooltip
                  title={isSelf ? "You cannot remove yourself" : "Remove user"}
                >
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => setIsRemoving(true)}
                      disabled={isSelf}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
            </Grid>
          )}
        </Grid>
      </MuiListItem>
      <Divider />
    </>
  );
};

const UserManagementPanel = ({
  employees = [],
  currentUser,
  isCurrentUserAdmin,
  onRoleChange,
  onRemove
}) => {
  const confirmedUsers = employees.filter(({ authUid }) => authUid);

  return (
    <List disablePadding>
      {confirmedUsers.map((user) => (
        <UserRow
          key={user.id}
          user={user}
          currentUser={currentUser}
          isCurrentUserAdmin={isCurrentUserAdmin}
          onRoleChange={onRoleChange}
          onRemove={onRemove}
        />
      ))}
      {!confirmedUsers.length && (
        <Box py={2} display="flex" justifyContent="center">
          <Typography variant="body2" color="textSecondary">
            No users yet.
          </Typography>
        </Box>
      )}
    </List>
  );
};

export default UserManagementPanel;
